import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import useAxios from 'axios-hooks';
import { useTranslation } from 'react-i18next';

import { useLocale } from '@util/providers/Locale';
import {
  DEFAULT_SUBSCRIPTION_CURRENCY,
  getSupportedSubscriptionCurrency,
} from '@config/subscriptionCurrencies';

import { useStateWithSessionStorage } from '../hooks/useStateWithSessionStorage';
import { getProductCatalog } from '@util/Plans/PlanFunctions';

const PlanContext = createContext({});

const defaultValues = {
  termIsYearly: true,
  trialLength: 30,
  plans: null,
  selectedPlanKey: null,
  selectedRateKey: null,
};

function PlanProvider({ children }) {
  const { i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage || i18n.language;
  const [plans, setPlans] = useState(defaultValues.plans);
  const [termIsYearly, setTermIsYearly] = useState(defaultValues.termIsYearly);
  const [trialLength, setTrialLength] = useStateWithSessionStorage(
    'trialLength',
    defaultValues.trialLength
  );
  const [selectedPlanKey, setSelectedPlanKey] = useStateWithSessionStorage(
    'selectedPlanKey',
    defaultValues.selectedPlanKey
  );
  const [selectedRateKey, setSelectedRateKey] = useStateWithSessionStorage(
    'selectedRateKey',
    defaultValues.selectedRateKey
  );

  const { selectedCurrency, locale, selectedLanguage, setSelectedCurrency } =
    useLocale();

  const supportedCatalogCurrency =
    getSupportedSubscriptionCurrency(selectedCurrency) ||
    DEFAULT_SUBSCRIPTION_CURRENCY;

  const pendingCatalogCurrency = useRef(null);
  const activeCatalogCurrency = useRef(supportedCatalogCurrency);
  const previousCatalogData = useRef(null);
  const selectedRateRef = useRef(null);

  const selectedPlan = useMemo(
    () => plans?.find((p) => p.key === selectedPlanKey) || null,
    [plans, selectedPlanKey]
  );

  const selectedRate = useMemo(() => {
    if (!selectedPlan) return null;
    if (selectedPlan.staticRatePlan) return selectedPlan.staticRatePlan;
    const matched =
      selectedRateKey &&
      selectedPlan.ratePlans.find((rp) => rp.key === selectedRateKey);
    if (matched) return matched;
    if (!selectedPlan.ratePlans) return null;
    return (
      selectedPlan.ratePlans.find((rp) =>
        termIsYearly ? rp.isYearly : rp.isMonthly
      ) || null
    );
  }, [selectedPlan, selectedRateKey, termIsYearly]);

  useEffect(() => {
    selectedRateRef.current = selectedRate;
  }, [selectedRate]);

  const setSelectedPlan = useCallback(
    (plan) => {
      if (!plan) {
        setSelectedPlanKey(defaultValues.selectedPlanKey);
        return;
      }
      setSelectedPlanKey(plan?.key || plan || defaultValues.selectedPlanKey);
    },
    [setSelectedPlanKey]
  );

  const setSelectedRate = useCallback(
    (rate) => {
      if (!rate) {
        setSelectedRateKey(defaultValues.selectedRateKey);
        return;
      }
      setSelectedRateKey(rate?.key || rate || defaultValues.selectedRateKey);
    },
    [setSelectedRateKey]
  );

  const [contextValue, setContextValue] = useState({
    plans,
    setPlans,
    selectedPlan,
    setSelectedPlan,
    selectedRate,
    setSelectedRate,
    setSelectedRateByPlan: (plan) => {
      if (!plan) {
        setSelectedRate(null);
        return;
      }
      if (plan.staticRatePlan) {
        setSelectedRate(plan.staticRatePlan);
      } else {
        const currentRate = selectedRateRef.current;
        if (plan && currentRate) {
          //* default to monthly rate plan if the previous rate is not a trial or yearly rate
          let rateType = 'isYearly';
          if (currentRate.isTrial) rateType = 'isTrial';
          if (currentRate.isYearly === false) rateType = 'isMonthly';

          const matchedRate = plan.ratePlans.find((rp) => rp[rateType]);
          setSelectedRate(matchedRate || null);
        } else {
          setSelectedRate(null);
        }
      }
    },
    termIsYearly,
    setTermIsYearly,
    resetSelectedPlan: () => setSelectedPlan(null),
    resetSelectedRate: () => setSelectedRate(null),
  });

  const [
    // eslint-disable-next-line
    { data: catalogData, loading: loadingCatalog, error: catalogError },
    getCatalog,
  ] = useAxios(
    supportedCatalogCurrency
      ? `${
          process.env.SERVICE_URL
        }subscriptions/catalog/${supportedCatalogCurrency.toLowerCase()}`
      : '',
    {
      manual: true,
    }
  );

  useEffect(() => {
    setContextValue((contextValue) => ({
      ...contextValue,
      selectedPlan,
      selectedRate,
      trialLength,
      termIsYearly,
    }));
  }, [selectedPlan, selectedRate, termIsYearly, trialLength]);

  //* fetch plans when currency changes
  useEffect(() => {
    if (
      locale?.localeDetails?.defaultToSubscription === true &&
      locale?.localeDetails?.isoCode !== 'CA'
    ) {
      setSelectedCurrency(DEFAULT_SUBSCRIPTION_CURRENCY);
    }

    if (supportedCatalogCurrency) {
      pendingCatalogCurrency.current = supportedCatalogCurrency;
      const controller = new AbortController();

      getCatalog({ signal: controller.signal }).catch((err) => {
        if (
          err?.code === 'ERR_CANCELED' ||
          err?.message === 'canceled' ||
          err?.__CANCEL__ === true
        ) {
          if (pendingCatalogCurrency.current === supportedCatalogCurrency) {
            pendingCatalogCurrency.current = null;
          }
          return; // ignore aborts
        }
        pendingCatalogCurrency.current = null;
      });

      return () => controller.abort(); // abort on unmount/navigation/HMR
    }

    pendingCatalogCurrency.current = null;
  }, [
    locale,
    selectedLanguage,
    selectedCurrency,
    supportedCatalogCurrency,
    setSelectedCurrency,
    getCatalog,
  ]);

  //* set catalog when new data is received from the service
  useEffect(() => {
    if (!catalogData) return;

    const catalogChanged = previousCatalogData.current !== catalogData;
    if (catalogChanged) {
      previousCatalogData.current = catalogData;
    }

    const currencyForCatalog = catalogChanged
      ? pendingCatalogCurrency.current ||
        activeCatalogCurrency.current ||
        DEFAULT_SUBSCRIPTION_CURRENCY
      : activeCatalogCurrency.current || DEFAULT_SUBSCRIPTION_CURRENCY;

    const productCatalog = getProductCatalog(catalogData, currencyForCatalog);
    setPlans(productCatalog());
    setTrialLength(catalogData.trialDays);

    if (catalogChanged) {
      activeCatalogCurrency.current = currencyForCatalog;
      pendingCatalogCurrency.current = null;
    }
  }, [catalogData, resolvedLanguage, setPlans, setTrialLength]);

  //* log catalog load failures without tearing down the page
  useEffect(() => {
    if (catalogError && typeof window !== 'undefined') {
      console.error('Error retrieving plan catalog.', catalogError);
    }
  }, [catalogError]);

  //* update context
  useEffect(() => {
    setContextValue((contextValue) => ({
      ...contextValue,
      plans,
    }));
  }, [plans]);

  return (
    <PlanContext.Provider value={contextValue}>{children}</PlanContext.Provider>
  );
}

function usePlan() {
  const context = useContext(PlanContext);
  if (context === undefined) {
    throw new Error('usePlan must be used within a PlanProvider');
  }

  return context;
}

export { PlanProvider, usePlan };
